import { Component, OnInit, Input, OnChanges, Output, EventEmitter } from '@angular/core';
import { AbstractAddComponent } from '../../../shared/abstracts/add/abstract-add/abstract-add.component';
import { ICarePlan, IPatient, IAnnotation } from '@ahryman40k/ts-fhir-types/lib/R4';
import { FormGroup, FormControl, FormArray, Validators } from '@angular/forms';
import { PeriodValidator } from '../../../shared/validators/valid-period.validator';
import { ModalController } from '@ionic/angular';
import { TherapyDiscontinuationComponent } from '../therapy-discontinuation/therapy-discontinuation.component';
import { AssociativeArray } from '../../../user/login/shared/ass-array.model';
import { TranslatePipe } from '../../../shared/translate/pipes/translate.pipe';
import { AngularEditorConfig } from '@kolkov/angular-editor';

@Component({
  selector: 'inc-general-care-plan',
  templateUrl: './general-care-plan.component.html',
  styleUrls: ['./general-care-plan.component.scss'],
})
export class GeneralCarePlanComponent extends AbstractAddComponent<ICarePlan> implements OnInit, OnChanges {
  @Input() patient: IPatient;
  @Input() user: any;
  @Output() callHistory: EventEmitter<any> = new EventEmitter();

  modalComponents: AssociativeArray = {
    therapyDiscontinuation: () => TherapyDiscontinuationComponent,
  };

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    translate: 'no',
    defaultFontName: 'Arial',
    defaultFontSize: '3',
    fonts: [
      {class: 'arial', name: 'Arial'},
      {class: 'courier-new', name: 'Courier New'},
      {class: 'helvetica', name: 'Helvetica'},
      {class: 'times-new-roman', name: 'Times New Roman'},
      {class: 'calibri', name: 'Calibri'},
    ],
    toolbarPosition: 'top',
  };

  constructor(public modalController: ModalController, private translate: TranslatePipe) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  ngOnChanges() {
    this.init();
  }

  init() {
    if (this.inData) {
      this.inData.period.start = this.inData.period.start && (this.inData.period.start as any).seconds ?
        new Date((this.inData.period.start as any).seconds * 1000).toISOString() :
        (this.inData.period.start !== '' ? this.inData.period.start : '');
      this.inData.period.end = this.inData.period.end && (this.inData.period.end as any).seconds ?
        new Date((this.inData.period.end as any).seconds * 1000).toISOString() :
        (this.inData.period.end !== '' ? this.inData.period.end : '');
      this.form.patchValue(this.inData);
    }
    this.form.controls.subject.setValue({ display: this.patient.name[0].text, reference: this.patient.id });
    this.form.controls.author.setValue({ reference: this.user.uid, display: this.user.displayName });
    this.form.setAsyncValidators(PeriodValidator.isValidPeriod());
  }

  save(inData: ICarePlan) {
    if (this.inData && this.inData.description !== '' && this.inData.period.start !== '' && this.inData.period.end !== '') {
      const note: IAnnotation = {
        authorReference: this.inData.author,
        time: this.inData.meta.lastUpdated,
        text: this.inData.description + '%' + this.inData.period.start + '%' + this.inData.period.end
      };
      inData.note = this.inData.note.length ? this.inData.note : [];
      inData.note.push(note);
    }
    if (inData.description && inData.status === 'unknown') {
      inData.status = 'active';
    }
    inData.meta.lastUpdated = new Date().toISOString();
    this.inData ? this.callUpdate.emit(inData) : this.callSave.emit(inData);
    this.form.reset(this.form.value);
  }

  openHistory() {
    this.callHistory.emit();
  }

  resetForm() {
    return new FormGroup({
      id: new FormControl(),
      title: new FormControl('BASIC_THERAPY_PLAN'),
      status: new FormControl('unknown'),
      intent: new FormControl('proposal'),
      meta: new FormGroup({
        lastUpdated: new FormControl(new Date().toISOString())
      }),
      category: new FormArray([
        new FormGroup({
          coding: new FormArray([
            new FormGroup({
              code: new FormControl('56851-9'),
              display: new FormControl('Care process or plan'),
              system: new FormControl('http://loinc.org')
            })]),
          text: new FormControl('BASIC_THERAPY_PLAN')
        })
      ]),
      subject: new FormGroup({
        reference: new FormControl(''),
        display: new FormControl('')
      }),
      description: new FormControl(''),
      created: new FormControl(new Date().toISOString()),
      author: new FormGroup({
        reference: new FormControl(''),
        display: new FormControl('')
      }),
      period: new FormGroup({
        start: new FormControl('', Validators.required),
        end: new FormControl('', Validators.required)
      }),
      note: new FormArray([]),
      activity: new FormArray([])
    });
  }

  async openModal(componentName: string) {
    const modal = await this.modalController.create({
      component: this.modalComponents[componentName](),
    });

    modal.onDidDismiss().then(detail => {
      if (detail.data) {
        if (detail.data.value && detail.data.value.therapyTermination) {
          const carePlan = this.form.value;
          carePlan.status = 'revoked';
          carePlan.description = this.translate.transform(detail.data.value.therapyTermination.causes)
            + '\n' + detail.data.value.therapyTermination.comments;
          carePlan.period.start = '';
          carePlan.period.end = '';
          this.save(carePlan);
        }
      }
    });
    return await modal.present();
  }

  activateUser() {
    const carePlan = this.form.value;
    carePlan.status = 'unknown';
    carePlan.description = '';
    this.save(carePlan);
  }
}
